<template>
  <div>
    <apexchart
      v-if="companyCategories.length"
      type="bar"
      :height="Math.max(300, companyCategories.length * 44 + 80)"
      :options="companiesOpts"
      :series="companiesSeries"
    />
    <empty-state
      v-else
      icon="mdi-domain"
      :title="$t('no_data')"
    />
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import EmptyState from "@/components/global/EmptyState.vue";
import restaurantService from "@/services/restaurant";

const SHIFT_COLORS = ["#1E88E5", "#43A047", "#FB8C00", "#8E24AA"];

export default {
  name: "GroupMealsCompaniesChart",
  components: { apexchart: VueApexCharts, EmptyState },
  props: {
    orders: { type: Array, default: () => [] },
  },
  data() {
    return {
      companies: [],
    };
  },
  created() {
    const restaurantId = this.$store.state.restaurant?.id;
    if (restaurantId) {
      restaurantService
        .getGroupType(restaurantId)
        .then((res) => {
          this.companies = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {});
    }
  },
  computed: {
    shiftLabels() {
      return [
        this.$t("1st_shift"),
        this.$t("2nd_shift"),
        this.$t("3rd_shift"),
        this.$t("4th_shift"),
      ];
    },
    companyNameMap() {
      const map = {};
      for (const c of this.companies) {
        map[c.id] = this.toLatin(c.name);
      }
      return map;
    },
    companyAgg() {
      const map = {};
      for (const item of this.orders) {
        const cid = item.company_id;
        if (!cid) continue;
        if (!map[cid]) map[cid] = [0, 0, 0, 0];
        if (item.shift_1 !== undefined) {
          map[cid][0] += Number(item.shift_1 || 0);
          map[cid][1] += Number(item.shift_2 || 0);
          map[cid][2] += Number(item.shift_3 || 0);
          map[cid][3] += Number(item.shift_4 || 0);
        } else {
          const s = (item.shift || 1) - 1;
          if (s >= 0 && s < 4) map[cid][s] += Number(item.quantity || 0);
        }
      }
      return map;
    },
    companyCategories() {
      return Object.entries(this.companyAgg)
        .map(([cid, shifts]) => ({
          cid,
          name: this.companyNameMap[cid] || `#${cid}`,
          total: shifts.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.total - a.total)
        .map((c) => c.name);
    },
    companiesSeries() {
      const orderedIds = Object.entries(this.companyAgg)
        .map(([cid, shifts]) => ({ cid, total: shifts.reduce((a, b) => a + b, 0) }))
        .sort((a, b) => b.total - a.total)
        .map((c) => c.cid);
      return this.shiftLabels.map((label, i) => ({
        name: label,
        data: orderedIds.map((cid) => this.companyAgg[cid]?.[i] || 0),
      }));
    },
    companiesOpts() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          fontFamily: "Roboto, sans-serif",
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "65%" } },
        colors: SHIFT_COLORS,
        xaxis: { categories: this.companyCategories, labels: { style: { fontSize: "12px" } } },
        yaxis: { labels: { style: { fontSize: "12px" }, maxWidth: 200 } },
        legend: { position: "bottom", horizontalAlign: "center" },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
        fill: { opacity: 1 },
        grid: { borderColor: "#f1f1f1" },
        responsive: [
          {
            breakpoint: 600,
            options: {
              yaxis: { labels: { style: { fontSize: "11px" }, maxWidth: 130 } },
              xaxis: { labels: { style: { fontSize: "11px" } } },
              legend: { fontSize: "12px", itemMargin: { horizontal: 6 } },
              plotOptions: { bar: { barHeight: "55%" } },
            },
          },
        ],
      };
    },
  },
};
</script>
